import { useEffect, useRef, useState } from 'react';
import { downloadEvents } from '../download';
import { eventLabel, type DeskEvent } from '../events';

export interface UnsyncedModalProps {
  title: string;
  body: string;
  events: DeskEvent[];
  /** Label + handler for the non-destructive way out (stay signed in, sign
   * in again, sign out to let the owner sign in…). */
  primary?: { label: string; onClick: () => void };
  discardLabel: string;
  /** Second-step button text; defaults to "Yes — discard N events". */
  confirmLabel?: string;
  onDiscard: () => void | Promise<void>;
}

/** Blocks a wipe while unsynced desk events exist (plan "Offline": "blocked
 * behind a modal if unsynced mutations exist"). Discarding needs a second,
 * explicit confirmation. */
export function UnsyncedModal({ title, body, events, primary, discardLabel, confirmLabel, onDiscard }: UnsyncedModalProps) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstButton.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-lg">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="unsynced-title"
        aria-describedby="unsynced-body"
        className="w-full max-w-lg rounded-card border border-border bg-surface p-xl shadow-card"
      >
        <h2 id="unsynced-title" className="text-h2 font-extrabold text-ink">
          {title}
        </h2>
        <p id="unsynced-body" className="mt-sm text-body text-ink-2">
          {body}
        </p>
        <ul className="mt-md max-h-40 overflow-auto rounded-input border border-hair p-sm text-small text-ink">
          {events.map((event) => (
            <li key={event.clientEventId}>
              {event.reference} · {event.guestName} — {eventLabel(event)} ({new Date(event.capturedAt).toLocaleString()})
            </li>
          ))}
        </ul>
        <div className="mt-lg flex flex-wrap gap-sm">
          {primary ? (
            <button
              ref={firstButton}
              type="button"
              onClick={primary.onClick}
              className="rounded-button bg-canyon px-md py-xs text-body font-semibold text-white"
            >
              {primary.label}
            </button>
          ) : null}
          <button
            ref={primary ? undefined : firstButton}
            type="button"
            onClick={() => downloadEvents(events)}
            className="rounded-button border border-border bg-surface px-md py-xs text-body text-ink"
          >
            Download a copy
          </button>
          {confirming ? (
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onDiscard();
                } finally {
                  setBusy(false);
                  setConfirming(false);
                }
              }}
              className="rounded-button bg-danger px-md py-xs text-body font-semibold text-white"
            >
              {confirmLabel ?? `Yes — discard ${events.length} ${events.length === 1 ? 'event' : 'events'}`}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="rounded-button border border-danger px-md py-xs text-body text-danger"
            >
              {discardLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
